import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { apiService } from '../services/api';
import { Website } from '../types';
import { Plus, Edit, Trash2, Globe, Calendar, Copy, Check } from 'lucide-react';
import WebsiteForm from './WebsiteForm';

const WebsiteList: React.FC = () => {
  const { t } = useLanguage();
  const [websites, setWebsites] = useState<Website[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingWebsite, setEditingWebsite] = useState<Website | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const fetchWebsites = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getWebsites();
      setWebsites(response.websites);
    } catch (err: any) {
      setError(err.response?.data?.message || t('websites.failedToFetch'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchWebsites();
  }, [fetchWebsites]);

  const handleCreate = () => {
    setEditingWebsite(null);
    setShowForm(true);
  };

  const handleEdit = (website: Website) => {
    setEditingWebsite(website);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('websites.confirmDelete'))) {
      return;
    }

    try {
      await apiService.deleteWebsite(id);
      await fetchWebsites();
    } catch (err: any) {
      setError(err.response?.data?.message || t('websites.failedToDelete'));
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingWebsite(null);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingWebsite(null);
    fetchWebsites();
  };

  const handleCopyWebsiteId = async (websiteId: string) => {
    try {
      await navigator.clipboard.writeText(websiteId);
      setCopiedId(websiteId);
      setTimeout(() => setCopiedId(null), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy website ID:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">{t('websites.title')}</h1>
        <button
          onClick={handleCreate}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('websites.addWebsite')}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {websites.length === 0 ? (
        <div className="text-center py-12">
          <Globe className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('websites.noWebsites')}</h3>
          <p className="mt-1 text-sm text-gray-500">
            {t('websites.createFirstWebsite')}
          </p>
          <div className="mt-6">
            <button
              onClick={handleCreate}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('websites.addWebsite')}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {websites.map((website) => (
            <div
              key={website.id}
              className="bg-white overflow-hidden shadow rounded-lg border border-gray-200"
            >
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Globe className="h-8 w-8 text-indigo-600" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        {website.name}
                      </h3>
                      <p className="text-sm text-gray-500">{website.domain}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(website)}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(website.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {website.description && (
                  <p className="mt-2 text-sm text-gray-600">{website.description}</p>
                )}
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-xs font-medium text-gray-500 mr-2">{t('websites.websiteId')}:</span>
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono text-gray-800">
                      {website.website_id}
                    </code>
                  </div>
                  <button
                    onClick={() => handleCopyWebsiteId(website.website_id)}
                    className="flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                    title={t('websites.copyWebsiteId')}
                  >
                    {copiedId === website.website_id ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <div className="mt-4 flex items-center text-sm text-gray-500">
                  <Calendar className="h-4 w-4 mr-1" />
                  Created {new Date(website.created_at).toLocaleDateString()}
                </div>
                <div className="mt-2">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      website.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {website.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <WebsiteForm
          website={editingWebsite}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default WebsiteList;
